import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SimpleChanges } from "@angular/core";
import { AutofillEvent, CdkAutofill } from "@angular/cdk/text-field";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { HotToastService } from "@ngneat/hot-toast";
import {
  SharedDataService,
  SubscriptionType,
} from "src/app/dashboard/shared-data.service";
import { TaggingService } from "src/app/dashboard/tagging.service";
import { LoginService } from "src/app/login/login.service";
import { CreateService } from "src/app/registration/create/create.service";
import { RegistrationService } from "src/app/registration/registration.service";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxIntlTelInputModule } from "@justin-s/ngx-intl-tel-input";
import { FormsModule } from "@angular/forms";
import { NgClass } from "@angular/common";
import { HttpClient } from "@angular/common/http"; // <-- New import
import { firstValueFrom } from "rxjs"; // <-- New import
import { RecaptchaModule } from "ng-recaptcha";
import { environment } from "src/environments/environment"; // <-- New import
import { AccountantService } from "src/app/dashboard/main/accountant/accountant.service";

export {};
declare global {
  interface Window {
    Calendly: any;
    dataLayer: any[];
    fpr?: (event: string, data: { email: string }) => void;
  }
}
declare var Stripe: any;
@Component({
  selector: "app-trial-drawer",
  templateUrl: "./trial-drawer.component.html",
  styleUrls: ["./trial-drawer.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    CdkAutofill,
    NgxIntlTelInputModule,
    NgClass,
    RecaptchaModule,
  ],
})
export class TrialDrawerComponent implements OnInit {
  show_loading: boolean = false;
  /** Hard lock against double/triple Start trial clicks while async work runs. */
  isSubmittingTrial = false;
  recaptchaV2Required: boolean = false;

  shortTrial: boolean = false;
  currStep: number = 1;

  @Output() signUpDrawerToggle = new EventEmitter<boolean>();

  public teamSize = "";
  accountingCompanyName = "";
  ownerFirstName = "";
  ownerLastName = "";
  ownerEmail = "";
  ownerPhone = "";
  referralCode = "";
  password = "";
  confirmpassword = "";
  xeroSignupToken: string | null = null;

  // UTM / campaign capture (from URL, hidden fields for HubSpot/GTM)
  utmSource = "";
  utmMedium = "";
  utmCampaign = "";
  utmTerm = "";
  utmContent = "";

  validEmail = true;
  lastValidatedEmail = "";
  emailErrorMessage = "";

  isCollapsed = true;

  // Stripe Elements state
  private stripe: any;
  private elements: any;
  private card: any;
  cardError: string = "";
  private cardInitialized = false;

  constructor(
    private createService: CreateService,
    private localStorage: LocalStorageService,
    private loginService: LoginService,
    private toastr: HotToastService,
    private taggingService: TaggingService,
    private http: HttpClient, // <-- Inject HttpClient
    private accountantService: AccountantService,
    public sharedDataService: SharedDataService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadRecaptchaScript();
    this.captureUtmFromUrl();
    this.applyXeroSignupIdentity();
  }

  private applyXeroSignupIdentity(): void {
    if (this.route.snapshot.queryParamMap.get("xero_signup_error")) {
      this.toastr.error("We couldn't sign you up with Xero. Please try again.");
      return;
    }

    const token = this.route.snapshot.queryParamMap.get("xero_signup");
    if (!token) return;
    this.xeroSignupToken = token;

    this.http
      .get<any>(`${environment.apiUrl}/integration/xero/signup/identity`, {
        params: { token },
      })
      .subscribe({
        next: (identity) => {
          if (identity.existing_account) {
            this.toastr.info(
              "An account already exists for your Xero email. Please sign in.",
            );
            this.router.navigate(["/login"], {
              queryParams: { ref: btoa(identity.email) },
            });
            return;
          }
          this.ownerEmail = identity.email || "";
          this.ownerFirstName = identity.first_name || "";
          this.ownerLastName = identity.last_name || "";
          this.toastr.success("Your details were imported from Xero.");
        },
        error: (error) =>
          this.toastr.error(
            error.error?.message || "We couldn't load your details from Xero.",
          ),
      });
  }

  /**
   * Parse UTM (and gclid) from query string so they can be sent as hidden fields with the trial signup.
   * Supports both ?utm_source=... and #/trial?utm_source=... style URLs.
   */
  private captureUtmFromUrl(): void {
    const params = new URLSearchParams(
      window.location.search ||
        (window.location.hash && window.location.hash.includes("?")
          ? window.location.hash.split("?")[1] || ""
          : ""),
    );
    this.utmSource = params.get("utm_source") || "";
    this.utmMedium = params.get("utm_medium") || "";
    this.utmCampaign = params.get("utm_campaign") || "";
    this.utmTerm = params.get("utm_term") || "";
    this.utmContent = params.get("utm_content") || "";
  }

  loadRecaptchaScript() {
    const scriptId = "recaptcha-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src =
        "https://www.google.com/recaptcha/api.js?render=6LfelxgrAAAAAOHRT5W6F4QUlSlxk73vczGuNIuG"; // update as needed
      script.async = true;
      document.head.appendChild(script);
    }
  }

  async executeRecaptcha(action: string): Promise<boolean> {
    if (environment.envName === "development") {
      // Skip recaptcha in development
      return true;
    }
    try {
      const token = await (window as any).grecaptcha.execute(
        "6LfelxgrAAAAAOHRT5W6F4QUlSlxk73vczGuNIuG",
        { action },
      );
      const recaptchaResult: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/api/verify-captcha`, { token }),
      );
      if (!recaptchaResult.success || recaptchaResult.score < 0.5) {
        this.toastr.error(
          "Suspicious activity detected. Please complete the reCAPTCHA challenge.",
        );
        this.recaptchaV2Required = true;
        return false;
      }
      return true;
    } catch (err) {
      this.toastr.error("Recaptcha verification failed");
      this.recaptchaV2Required = true;
      return false;
    }
  }

  @HostListener("window:message", ["$event"]) onPostMessage(event) {
    if (
      this.isCalendlyEvent(event) &&
      event.data.event == "calendly.event_scheduled"
    ) {
      console.log("Calendly Scheduled");
      this.moveToStep2();
    }
  }

  isCalendlyEvent(e: any) {
    return e.data.event && e.data.event.indexOf("calendly") === 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    changes.signUpDrawerOpen.currentValue
      ? (document.body.style.overflow = "hidden")
      : (document.body.style.overflow = "auto");

    if (document.getElementById("firstname") != null) {
      document.getElementById("firstname").focus();
    }
    console.log("ngOnChanges");
  }

  ngOnDestroy(): void {
    this.closeDrawer();
  }

  @HostListener("document:keydown.escape", ["$event"]) onKeydownHandler(
    event: KeyboardEvent,
  ) {
    this.closeDrawer();
  }

  focusFirstName() {
    if (document.getElementById("firstname") != null) {
      document.getElementById("firstname").focus();
    }
  }

  /**
   * Browsers often autofill the DOM without firing input/change, so ngModel
   * stays empty and validateForm() keeps Continue disabled. CDK detects
   * autofill; we then copy current input values into the bound fields.
   */
  onAutofill(event: AutofillEvent): void {
    if (!event.isAutofilled) {
      return;
    }
    this.syncFormFieldsFromDom();
  }

  private syncFormFieldsFromDom(): void {
    const read = (id: string): string => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      return el?.value ?? "";
    };

    this.accountingCompanyName = read("firmname");
    this.ownerFirstName = read("firstname");
    this.ownerLastName = read("lastname");
    this.ownerEmail = read("email");
    this.ownerPhone = read("phone");
    this.password = read("password");
    this.confirmpassword = read("confirmpassword");
    this.referralCode = read("referral");
  }

  validateEmail(email: string) {
    const validRegex = String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
    // true = disable the button, so we check the opposite
    return (
      (this.lastValidatedEmail == this.ownerEmail && !this.validEmail) ||
      !validRegex
    );
  }

  validateForm() {
    return (
      this.accountingCompanyName == "" ||
      this.ownerFirstName == "" ||
      this.ownerLastName == "" ||
      this.ownerPhone == "" ||
      this.validateEmail(this.ownerEmail)
    );
  }

  hasCapitalLetter() {
    return String(this.password).match(/([A-Z])/);
  }

  hasLowerCaseLetter() {
    return String(this.password).match(/([a-z])/);
  }

  hasSymbols() {
    return String(this.password).match(/[-!@$%#^&*()_+|~=`{}\[\]:";'<>?,.\/]/);
  }

  hasNumbers() {
    return String(this.password).match(/([0-9])/);
  }

  hasEightChars() {
    return this.password.length >= 8;
  }

  isConfirmPasswordSame() {
    return this.password && this.password == this.confirmpassword;
  }

  validatePassword() {
    return !(
      this.hasCapitalLetter() &&
      this.hasLowerCaseLetter() &&
      this.hasSymbols() &&
      this.hasNumbers() &&
      this.hasEightChars() &&
      this.isConfirmPasswordSame()
    );
  }

  private failTrialSubmission(message: string) {
    this.show_loading = false;
    this.isSubmittingTrial = false;
    this.toastr.error(message);
  }

  private userCreateFailureMessage(payload: any): string {
    return (
      payload?.message ||
      "We couldn't create your account. Please try again, or sign in if you already started a trial."
    );
  }

  async createTrialAccount(stripeToken?: any) {
    if (this.validatePassword()) {
      this.failTrialSubmission("Please fix the password requirements.");
      return;
    }

    this.ensureFreshLogin();

    await this.taggingService.tagAnonymousUserPromise(
      this.ownerEmail,
      this.ownerFirstName,
      this.ownerLastName,
      null,
      null,
      this.ownerPhone,
    );

    if (this.referralCode) {
      try {
        await this.createService.validate_promo_code(this.referralCode);
      } catch ({ error }) {
        this.failTrialSubmission(error.message);
        return;
      }
    }

    let addUserResponse: any;
    try {
      addUserResponse = await this.createService.add_user(
        this.ownerFirstName,
        this.ownerLastName,
        this.ownerEmail,
        this.password,
        1,
        1,
        null,
        null,
        this.xeroSignupToken,
      );
    } catch (err: any) {
      this.failTrialSubmission(
        this.userCreateFailureMessage(err?.error) ||
          "Failed to create user. Please try again.",
      );
      return;
    }

    // Backend historically returned HTTP 200 with only { message } on validation failure
    if (!addUserResponse?.user || !addUserResponse?.data) {
      this.failTrialSubmission(this.userCreateFailureMessage(addUserResponse));
      return;
    }

    this.toastr.success("User created!");
    this.localStorage.remove("temp_email");
    this.localStorage.remove("temp_first_name");
    this.localStorage.remove("temp_last_name");
    this.localStorage.remove("access_token");
    this.localStorage.remove("refresh_token");
    this.localStorage.remove("data");
    this.localStorage.remove("user");
    this.localStorage.remove("company");
    this.localStorage.remove("pin_expires");

    this.localStorage.set("data", addUserResponse.data);
    this.localStorage.set("user", addUserResponse.user);
    this.localStorage.set("remember", true);

    let loginResponse: any;
    try {
      loginResponse = await this.loginService.login(
        this.ownerEmail,
        this.password,
      );
    } catch (err: any) {
      this.failTrialSubmission(
        err?.error?.detail ||
          err?.error?.message ||
          "Account was created, but login failed. Please sign in with the same email and password.",
      );
      return;
    }

    this.localStorage.set("access_token", loginResponse.access);
    this.localStorage.set("refresh_token", loginResponse.refresh);
    this.localStorage.set("company", loginResponse.company);
    this.localStorage.set("subscription_type", loginResponse.subscription_type);

    try {
      const addCompanyResponse: any = await this.createService.add_company(
        this.accountingCompanyName,
        null,
        true,
        null,
        SubscriptionType.PREMIUM,
        false,
        this.shortTrial,
        this.referralCode,
      );

      try {
        if (stripeToken) {
          await this.accountantService.add_payment_method(stripeToken);
        }
      } catch (err) {
        // Card attach failures shouldn't block the flow; show a toast and continue
        this.toastr.error(
          "Could not save card. You can add it later in billing.",
        );
      }

      if (addCompanyResponse) {
        this.localStorage.set("data", addCompanyResponse.data);
        this.localStorage.set("company", addCompanyResponse.company);
        this.localStorage.set("remember", true);

        const subscriptionType =
          addCompanyResponse.subscription_type ||
          loginResponse.subscription_type;
        // Avoid SharedDataService.subscriptionType setter — it can reload the
        // page when leftover session subscription differs from the new account.
        this.sharedDataService.setSubscriptionTypeWithoutReload(
          subscriptionType,
        );
        this.sharedDataService.data = addCompanyResponse.data;
        this.sharedDataService.company = addCompanyResponse.company;
        this.sharedDataService.user = addUserResponse.user;
      }

      if (typeof window.fpr === "function") {
        window.fpr("referral", { email: this.ownerEmail });
      }

      // Google Tag Manager: fire so marketing can configure tags on the signup form
      if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: "Trial_Started" });
      }
      this.hideRecaptchaBadge();

      // Keep loading / submit lock until redirect navigates away
      await this.loginService.loginToFirestore(true, true);
    } catch (error: any) {
      this.failTrialSubmission(
        error?.error?.message ||
          "We couldn't finish setting up your trial. Please try signing in, or contact support if this keeps happening.",
      );
    }
  }

  ensureFreshLogin() {
    this.localStorage.remove("data");
    this.localStorage.remove("user");
    this.localStorage.remove("company");
    this.localStorage.remove("remember");
    this.localStorage.remove("access_token");
    this.localStorage.remove("refresh_token");
    this.localStorage.remove("subscription_type");
    this.localStorage.remove("subscription_tier");
    this.sharedDataService.clearClientSession();
  }

  closeDrawer(): void {
    this.signUpDrawerToggle.emit(false);
  }

  tagAnonymousUser() {
    this.taggingService
      .tagAnonymousUser(
        this.ownerEmail,
        this.ownerFirstName,
        this.ownerLastName,
        null,
        null,
        this.ownerPhone,
      )
      .subscribe(() => {});
  }

  async moveToStep2(): Promise<void> {
    // Before proceeding, run recaptcha
    const valid = await this.executeRecaptcha("trial");
    if (!valid) {
      // recaptchaV2Required is set to true, so the v2 widget will display
      return;
    }

    // Validate referral code if present
    this.show_loading = true;
    if (this.referralCode) {
      const isReferralValid = await this.validateReferralCode();
      if (!isReferralValid) {
        this.show_loading = false;
        return;
      }
    }

    // Go to payment step and initialize Stripe Elements
    this.show_loading = false;
    this.currStep = 2;
    this.hideRecaptchaBadge();
    setTimeout(() => this.initStripeElements(), 0);
  }

  private async validateReferralCode(): Promise<boolean> {
    if (!this.referralCode) {
      return true; // No referral code provided, so it's valid
    }

    try {
      await this.createService.validate_promo_code(this.referralCode);
      return true;
    } catch (error) {
      this.toastr.error(error.error?.message || "Invalid referral code");
      return false;
    }
  }

  // Handler for when the reCAPTCHA v2 challenge is resolved.
  async onRecaptchaV2Resolved(token: string) {
    // Immediately proceed since backend validation is not required for V2
    this.recaptchaV2Required = false;
    this.show_loading = false;
    this.currStep = 2;
    setTimeout(() => this.initStripeElements(), 0);
  }

  moveToStep3(shortTrial = false): void {
    this.shortTrial = shortTrial;
    this.currStep = 2;
    setTimeout(() => this.initStripeElements(), 0);
  }

  // Helper method to remove the reCAPTCHA badge
  hideRecaptchaBadge() {
    const badge = document.querySelector(".grecaptcha-badge") as HTMLElement;
    if (badge) {
      badge.style.display = "none";
    }
  }

  private initStripeElements() {
    if (this.cardInitialized) {
      return;
    }
    try {
      this.stripe = (window as any).Stripe
        ? (window as any).Stripe(environment.stripeKey)
        : Stripe && Stripe(environment.stripeKey);
      if (!this.stripe) {
        this.toastr.error("Stripe.js not loaded");
        return;
      }
      this.elements = this.stripe.elements();
      const style = {
        base: {
          color: "#32325d",
          lineHeight: "18px",
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      } as any;
      this.card = this.elements.create("card", { style });
      const mountPoint = document.getElementById("trial-card-element");
      if (mountPoint) {
        this.card.mount("#trial-card-element");
        this.card.addEventListener("change", (event: any) => {
          this.cardError = event.error ? event.error.message : "";
        });
        this.cardInitialized = true;
      }
    } catch (err) {
      this.toastr.error("Failed to initialize payment form");
    }
  }

  async submitCardAndStartTrial() {
    // Prevent parallel submissions (overlay alone is not enough — clicks can
    // queue while stripe.createToken / createTrialAccount are in flight).
    if (this.isSubmittingTrial || this.show_loading) {
      return;
    }
    if (!this.card) {
      this.toastr.error("Payment form not ready");
      return;
    }

    this.isSubmittingTrial = true;
    this.show_loading = true;
    try {
      const result = await this.stripe.createToken(this.card);
      if (result.error) {
        this.cardError = result.error.message;
        this.failTrialSubmission(result.error.message || "Card error");
        return;
      }
      await this.createTrialAccount(result.token);
    } catch (err) {
      this.failTrialSubmission("Failed to start trial");
    }
  }
}
